import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, GripVertical, Plus, Star, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const BLANK = { kind: "topic", value: "", label: "" };

// Up to three choices on MiLu's morning menu, named by the reader: topics or saved folders.
export const MorningChoice = ({ folders = [] }) => {
  const [mine, setMine] = useState([]);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/news/settings")
      .then(({ data }) => {
        const kept = data.morning_customs || [];
        setMine(kept);
        setRows(kept.length ? kept : [BLANK]);
      })
      .catch(() => {});
  }, []);

  const edit = (i, patch) => setRows((was) => was.map((r, n) => (n === i ? { ...r, ...patch } : r)));

  // drag a choice into the order it should be read
  const [held, setHeld] = useState(null);
  const dropOn = (i) => {
    if (held === null || held === i) return setHeld(null);
    setRows((was) => {
      const next = [...was];
      next.splice(i, 0, next.splice(held, 1)[0]);
      return next;
    });
    setHeld(null);
  };

  const save = async () => {
    const items = rows.filter((r) => r.value.trim());
    try {
      const { data } = await api.put("/news/morning/customs", { items });
      setMine(data.morning_customs);
      setRows(data.morning_customs.length ? data.morning_customs : [BLANK]);
      setOpen(false);
      toast.success(data.morning_customs.length
        ? `${data.morning_customs.length} of your own choices on the menu`
        : "Your own choices are off the menu");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not keep those choices");
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid="morning-choice">
      <button
        data-testid="morning-choice-toggle"
        onClick={() => setOpen((v) => !v)}
        className={`min-h-[34px] rounded-full border px-3.5 text-xs transition-colors ${
          mine.length ? "border-brand bg-brand/15 font-semibold text-brand"
                      : "border-white/10 text-ink-soft hover:border-brand/40"}`}
      >
        <Star className="me-1.5 inline h-3 w-3" />
        {mine.length ? `My choices: ${mine.map((m) => m.label).join(", ")}` : "Add my own choices"}
      </button>

      {open && (
        <span className="inline-flex flex-col gap-2 rounded-2xl border border-white/10 bg-panel px-3 py-2"
              data-testid="morning-choice-editor">
          {rows.map((row, i) => (
            <span
              key={`choice-${i}`}
              data-testid={`morning-choice-row-${i}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(i)}
              className={`inline-flex flex-wrap items-center gap-2 rounded-full transition-opacity ${
                held === i ? "opacity-40" : ""}`}
            >
              <span
                draggable
                onDragStart={() => setHeld(i)}
                onDragEnd={() => setHeld(null)}
                data-testid={`morning-choice-grip-${i}`}
                title="Drag to set the order they are read"
                className="cursor-grab text-ink-soft active:cursor-grabbing"
              >
                <GripVertical className="h-3.5 w-3.5" />
              </span>
              <select
                data-testid={`morning-choice-kind-${i}`}
                value={row.kind}
                onChange={(e) => edit(i, { kind: e.target.value, value: "" })}
                className="min-h-[30px] rounded-full border border-white/15 bg-black/40 px-2 text-xs"
              >
                <option value="topic">A topic</option>
                <option value="folder">A saved folder</option>
              </select>
              {row.kind === "folder" ? (
                <select
                  data-testid={`morning-choice-folder-${i}`}
                  value={row.value}
                  onChange={(e) => edit(i, { value: e.target.value })}
                  className="min-h-[30px] rounded-full border border-white/15 bg-black/40 px-2 text-xs"
                >
                  <option value="">Pick one…</option>
                  {folders.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid={`morning-choice-topic-${i}`}
                  value={row.value}
                  onChange={(e) => edit(i, { value: e.target.value })}
                  placeholder="Arsenal, electric cars…"
                  className="min-h-[30px] w-40 rounded-full border border-white/15 bg-black/40 px-3 text-xs outline-none focus:border-brand/60"
                />
              )}
              <input
                data-testid={`morning-choice-label-${i}`}
                value={row.label}
                onChange={(e) => edit(i, { label: e.target.value })}
                placeholder="Button name (optional)"
                className="min-h-[30px] w-36 rounded-full border border-white/15 bg-black/40 px-3 text-xs outline-none focus:border-brand/60"
              />
              <button
                data-testid={`morning-choice-drop-${i}`}
                onClick={() => setRows((was) => (was.length > 1
                  ? was.filter((_, n) => n !== i)
                  : [BLANK]))}
                aria-label="Take this one off"
                className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}

          <span className="inline-flex items-center gap-2">
            <span className="text-[10px] text-ink-soft">Drag to set the reading order</span>
            {rows.length < 3 && (
              <button
                data-testid="morning-choice-add"
                onClick={() => setRows((was) => [...was, BLANK])}
                className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
              >
                <Plus className="h-3 w-3" /> One more
              </button>
            )}
            <button
              data-testid="morning-choice-save"
              onClick={save}
              className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full bg-brand px-3 text-xs font-semibold text-white hover:bg-brand-hover"
            >
              <Check className="h-3 w-3" /> Keep them
            </button>
          </span>
        </span>
      )}
    </span>
  );
};
